import { z } from 'zod';
import { CLOSE_REASONS, EXECUTION_MODES, STRATEGY_KEYS } from '../enums';
import { decimalStringSchema, uuidSchema } from './common';

/**
 * GET /reports — closed trades in a date range with profit/loss, fees and the amounts
 * involved, plus totals, per-coin and per-period aggregates for the charts. Days/weeks/months
 * are cut in `tz` (default Europe/Istanbul), so a trade at 01:00 local time lands on the
 * right day even though everything is stored in UTC.
 */

export const REPORT_GRANULARITIES = ['day', 'week', 'month'] as const;
export type ReportGranularity = (typeof REPORT_GRANULARITIES)[number];

export const reportQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  mode: z.enum(EXECUTION_MODES).optional(),
  /** Only trades opened by this entry strategy; omitted = all strategies. */
  strategy: z.enum(STRATEGY_KEYS).optional(),
  granularity: z.enum(REPORT_GRANULARITIES).default('day'),
  tz: z.string().regex(/^[A-Za-z_]+(?:\/[A-Za-z_+-]+){0,2}$/).default('Europe/Istanbul'),
});
export type ReportQuery = z.infer<typeof reportQuerySchema>;

export const reportTotalsSchema = z.object({
  /** Sum of winning trades' PnL (≥ 0). */
  profit: decimalStringSchema,
  /** Sum of losing trades' PnL (≤ 0). */
  loss: decimalStringSchema,
  /** profit + loss — fees are already inside each trade's PnL. */
  net: decimalStringSchema,
  fees: decimalStringSchema,
  trades: z.number().int(),
  wins: z.number().int(),
  losses: z.number().int(),
  /** Σ amount spent on buys / received from sells, in USDT. */
  boughtValue: decimalStringSchema,
  soldValue: decimalStringSchema,
});
export type ReportTotals = z.infer<typeof reportTotalsSchema>;

export const reportGroupSchema = reportTotalsSchema.pick({ profit: true, loss: true, net: true, fees: true, trades: true, wins: true, losses: true });
export type ReportGroup = z.infer<typeof reportGroupSchema>;

export const reportTradeSchema = z.object({
  id: uuidSchema,
  symbol: z.string(),
  strategyKey: z.enum(STRATEGY_KEYS),
  openedAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime(),
  closeReason: z.enum(CLOSE_REASONS).nullable(),
  qty: decimalStringSchema,
  entryPrice: decimalStringSchema,
  exitPrice: decimalStringSchema.nullable(),
  /** What the buy cost (qty × entry price), fee excluded. */
  boughtValue: decimalStringSchema,
  /** What the sells brought in (Σ fill qty × price), fee excluded. */
  soldValue: decimalStringSchema,
  fees: decimalStringSchema,
  pnl: decimalStringSchema,
  pnlPct: decimalStringSchema,
  /** Total account value (USDT) just before the buy and just after the sell. */
  equityAtEntry: decimalStringSchema.nullable(),
  equityAtExit: decimalStringSchema.nullable(),
});
export type ReportTrade = z.infer<typeof reportTradeSchema>;

export const reportResponseSchema = z.object({
  mode: z.enum(EXECUTION_MODES),
  from: z.string().datetime().nullable(),
  to: z.string().datetime().nullable(),
  granularity: z.enum(REPORT_GRANULARITIES),
  tz: z.string(),
  totals: reportTotalsSchema,
  bySymbol: z.array(reportGroupSchema.extend({ symbol: z.string() })),
  /** Side-by-side result of each strategy in range — which one earned more. */
  byStrategy: z.array(reportGroupSchema.extend({ strategyKey: z.enum(STRATEGY_KEYS) })),
  strategy: z.enum(STRATEGY_KEYS).nullable(),
  /** `period` = first day of the day/week/month in `tz`, 'YYYY-MM-DD'. */
  byPeriod: z.array(reportGroupSchema.extend({ period: z.string() })),
  /** Newest first, at most 500 (totals always cover every trade in range). */
  trades: z.array(reportTradeSchema),
  truncated: z.boolean(),
});
export type ReportResponse = z.infer<typeof reportResponseSchema>;

/**
 * GET /reports/excursions — trade quality: how far each closed trade went in its favour (MFE)
 * and against it (MAE) while open, and where price was 2 hours after the exit. Tells an entry
 * problem (low MFE everywhere) from an exit problem (high MFE, then stopped out at a loss).
 */
export const excursionsQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  mode: z.enum(EXECUTION_MODES).optional(),
  strategy: z.enum(STRATEGY_KEYS).optional(),
  /** Newest closed trades first; each one may need a page of 5m bars from Binance. */
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ExcursionsQuery = z.infer<typeof excursionsQuerySchema>;

export const excursionTradeSchema = z.object({
  id: uuidSchema,
  symbol: z.string(),
  strategyKey: z.enum(STRATEGY_KEYS),
  closeReason: z.enum(CLOSE_REASONS).nullable(),
  openedAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime(),
  entryPrice: decimalStringSchema,
  exitPrice: decimalStringSchema.nullable(),
  pnlPct: decimalStringSchema,
  /** null when no 5m bars cover the trade (or it was held longer than one page of bars). */
  mfePct: decimalStringSchema.nullable(),
  maePct: decimalStringSchema.nullable(),
  /** Price change from the exit to `postExitMin` later; null until that bar has closed. */
  postExitPct: decimalStringSchema.nullable(),
});
export type ExcursionTrade = z.infer<typeof excursionTradeSchema>;

export const excursionGroupSchema = z.object({
  closeReason: z.enum(CLOSE_REASONS).nullable(),
  trades: z.number().int(),
  avgPnlPct: decimalStringSchema,
  avgMfePct: decimalStringSchema.nullable(),
  avgMaePct: decimalStringSchema.nullable(),
  avgPostExitPct: decimalStringSchema.nullable(),
});
export type ExcursionGroup = z.infer<typeof excursionGroupSchema>;

export const excursionsResponseSchema = z.object({
  mode: z.enum(EXECUTION_MODES),
  timeframe: z.literal('5m'),
  postExitMin: z.number().int(),
  trades: z.array(excursionTradeSchema),
  summary: z.object({ byCloseReason: z.array(excursionGroupSchema) }),
});
export type ExcursionsResponse = z.infer<typeof excursionsResponseSchema>;
